<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { uploadImage, uploadVideo } from "@/lib/uploads";
import RichTextField from "@/components/RichTextField.vue";
import { fetchUsers } from "@/features/users/api";
import { createPost, searchPlaces } from "./api";

const router = useRouter();
const posting = ref(false), error = ref(""), success = ref(false), uploadingMedia = ref(false), usersLoading = ref(false);
const users = ref([]), author = ref(null), postDesc = ref("");
const postImage = ref([]), displayImages = ref([]), postVideo = ref([]);
const placeName = ref(""), placeLink = ref(""), placeQuery = ref(""), places = ref([]), placesOpen = ref(false);

onMounted(async () => {
  usersLoading.value = true;
  try {
    const data = await fetchUsers();
    users.value = (Array.isArray(data) ? data : []).map(u => ({ ...u, _name: u.display_name || u.name || "" }));
  } catch { error.value = "Couldn't load users."; }
  finally { usersLoading.value = false; }
});

// Author autocomplete
const authorQuery = ref(""), authorOpen = ref(false);
const authorMatches = computed(() => {
  const q = authorQuery.value.trim().toLowerCase();
  return users.value.filter(u => !q || [u._name, u.name, u.company, u.department, u.customer_id].filter(Boolean).some(f => String(f).toLowerCase().includes(q))).slice(0, 50);
});
function pickAuthor(u) { author.value = u; authorQuery.value = u._name; authorOpen.value = false; }

// Media
async function addPhotos(e) {
  const files = Array.from(e.target.files || []); e.target.value = "";
  const valid = files.filter(f => /\.(jpe?g|png|webp|gif)$/i.test(f.name) && f.size / (1024 * 1024) < 500);
  if (valid.length !== files.length) { alert("One or more files are not a valid image or exceed 500MB."); return; }
  uploadingMedia.value = true;
  try { for (const file of valid) { const url = await uploadImage("posts", file); postImage.value.push(url); displayImages.value.push(url.replace("_500x500", "")); } }
  catch { error.value = "Couldn't upload an image."; }
  finally { uploadingMedia.value = false; }
}
async function addVideos(e) {
  const files = Array.from(e.target.files || []); e.target.value = "";
  uploadingMedia.value = true;
  try { for (const file of files) { if (!/\.(mp4|mov|mkv|flv|gif|avi|mpe?g)$/i.test(file.name)) { alert("File is not a video."); continue; } postVideo.value.push(await uploadVideo(file)); } }
  catch { error.value = "Couldn't upload a video."; }
  finally { uploadingMedia.value = false; }
}

// Location autocomplete
let placeTimer = null;
function onPlaceInput() {
  placesOpen.value = true; clearTimeout(placeTimer); const input = placeQuery.value;
  if (input.length <= 1) { places.value = []; return; }
  placeTimer = setTimeout(async () => { try { places.value = await searchPlaces(input); } catch { places.value = []; } }, 300);
}
function pickPlace(p) { placeName.value = p.description; placeLink.value = `https://www.google.com/maps/place/?q=place_id:${String(p.place_id).replace("&", " ")}`; placeQuery.value = p.description; placesOpen.value = false; }

// Hashtag / mention extraction
function extractHashtags(text) { const tags = [], re = /#\w+/g; let m; while ((m = re.exec(text)) != null) tags.push(m[0].replace("#", "")); return tags; }
function resolveMentions(text) {
  const tagged = [], re = /@\w+/g; let m;
  while ((m = re.exec(text)) != null) {
    const handle = m[0].replace("@", "").toLowerCase();
    const u = users.value.find(x => [x.value, x._name, x.name].filter(Boolean).some(f => String(f).toLowerCase() === handle));
    if (u && !tagged.some(e => e.userId === u.customer_id)) tagged.push({ userId: u.customer_id, username: u.value || u._name, userData: { company: u.company, customer_id: u.customer_id, department: u.department, display_name: u.display_name, email: u.email, image: u.image, job_title: u.job_title, level: u.level, objectID: u.objectID ?? u.id, value: u.value, tokenFCM: u.tokenFCM } });
  }
  return tagged;
}

async function submitPost() {
  error.value = ""; success.value = false;
  if (!author.value) { error.value = "Select a user to post as."; return; }
  if (!postDesc.value.trim()) { error.value = "Nothing to post."; return; }
  const u = author.value;
  const postData = { content: postDesc.value, author: u.display_name || u.name, authorId: u.objectID ?? u.id, comments: [], likes: [], createdAt: new Date(), updatedAt: new Date(), recommended: false, imgUrl: u.image, postImage: postImage.value, postVideo: postVideo.value, isPhoneUpdated: true, location: placeName.value, locationLink: placeLink.value, taggedPersons: resolveMentions(postDesc.value), _tags: extractHashtags(postDesc.value) };
  posting.value = true;
  try { await createPost(postData); success.value = true; setTimeout(() => router.push({ name: "posts" }), 800); }
  catch { error.value = "Couldn't create the post. Please try again."; }
  finally { posting.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'posts' })">Back</VBtn>
    <h1 class="text-h5 mb-4">Create Post</h1>
    <VCard class="pa-4" max-width="680">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Post created. Redirecting…</VAlert>

      <!-- Author picker -->
      <div class="mb-4">
        <div class="text-body-2 font-weight-semibold mb-1">Post as</div>
        <VAutocomplete
          v-model="author"
          v-model:search="authorQuery"
          :items="authorMatches"
          :item-title="u => u?._name || u?.name || ''"
          :item-value="u => u"
          :loading="usersLoading"
          label="Search by name, department, company or ID…"
          return-object
          auto-select-first
          no-filter
        >
          <template #item="{ item, props: p }">
            <VListItem v-bind="p" :subtitle="`${item.raw.customer_id || ''}${item.raw.department ? ' · ' + item.raw.department : ''}`" />
          </template>
        </VAutocomplete>
      </div>

      <!-- Content -->
      <RichTextField v-model="postDesc" label="What's on your mind?" class="mb-4" />

      <!-- Media previews -->
      <div v-if="displayImages.length" class="d-flex flex-wrap gap-2 mb-3">
        <div v-for="(src, i) in displayImages" :key="i" style="position:relative; display:inline-block">
          <img :src="src" style="max-height:120px; border-radius:6px" alt="" />
          <VBtn
            icon size="x-small" color="error"
            style="position:absolute; top:2px; right:2px; opacity:0.85"
            @click="displayImages.splice(i, 1); postImage.splice(i, 1)"
          ><VIcon size="14">ri-close-line</VIcon></VBtn>
        </div>
      </div>
      <div v-if="postVideo.length" class="d-flex flex-wrap gap-2 mb-3 align-center">
        <video v-for="(src, i) in postVideo" :key="i" :src="src" controls style="max-height:160px; border-radius:6px" />
        <VBtn variant="text" size="small" color="error" @click="postVideo = []">Remove Videos</VBtn>
      </div>

      <!-- Media buttons -->
      <div class="d-flex gap-3 flex-wrap mb-4 align-center">
        <label class="media-btn">
          📷 Add Photo / GIF
          <input type="file" multiple accept="image/jpeg,image/gif,image/png,image/webp" class="d-none" @change="addPhotos" />
        </label>
        <label class="media-btn">
          🎬 Add Video
          <input type="file" multiple accept="video/*" class="d-none" @change="addVideos" />
        </label>
        <span v-if="uploadingMedia" class="text-caption text-medium-emphasis">Uploading…</span>
      </div>

      <!-- Location -->
      <div class="mb-4" style="position:relative">
        <VTextField v-model="placeQuery" label="Add Location" placeholder="Search for a place…" @input="onPlaceInput" @focus="placesOpen = true" @blur="setTimeout(() => { placesOpen = false }, 200)" />
        <VCard v-if="placesOpen && places.length" class="location-list" elevation="4">
          <VList density="compact">
            <VListItem v-for="p in places" :key="p.place_id" :title="p.description" @click="pickPlace(p)" />
          </VList>
        </VCard>
        <p v-if="placeName" class="text-caption text-medium-emphasis mt-1">📍 {{ placeName }}</p>
      </div>

      <VBtn :loading="posting" :disabled="uploadingMedia" @click="submitPost">Post</VBtn>
    </VCard>
  </section>
</template>

<style scoped>
.media-btn {
  display: inline-flex; align-items: center; gap: 6px; cursor: pointer;
  padding: 6px 12px; border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px; font-size: 0.875rem;
}
.media-btn:hover { background: rgba(var(--v-border-color), 0.1); }
.location-list { position: absolute; z-index: 20; left: 0; right: 0; top: 56px; max-height: 220px; overflow-y: auto; }
</style>
